import '@afterpaytouch/core/design-token.css'
import { Loader, ThemeProvider, TooltipProvider, Theme, ModalProvider, useTheme } from '@afterpaytouch/core'
import { Kasada } from '@afterpaytouch/integrations/kasada'
import { getLaunchDarklyConfiguration, LDEvaluationDetail, withLDProvider } from '@afterpaytouch/feature'
import { getInitialDarkMode, getDarkModePreference, setDarkModePreference } from '../src/utils/dark-mode'
import nextI18NextConfig from '../next-i18next.config'
import type { NextComponentType, NextPageContext } from 'next'
import { appWithTranslation, UserConfig } from 'next-i18next'
import type { AppInitialProps } from 'next/app'
import type { AppContextType, AppPropsType } from 'next/dist/shared/lib/utils'
import type { Router } from 'next/router'
import router from 'next/router'
import React, { ReactElement, ReactNode, useEffect, useRef } from 'react'
import { parseCookies, setCookie } from 'nookies'
import { Container as ModalContainer } from 'react-modal-promise'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { Persistor } from 'redux-persist/es/types'
import { api, app, DEBUG_MODE } from '../src/env'
import { AmplitudeProvider } from '../src/integrations/amplitude'
import { DyScan } from '../src/integrations/dyScan'
import { GoogleTagManager } from '../src/integrations/googletagmanager'
import { OneTrust } from '../src/integrations/oneTrust'
import { MainLayout } from '../src/layouts/MainLayout'
import { APT_PRELOGIN_USER_ID, APT_USER_ID } from '../src/model'
import {
  makeStore,
  persistor,
  profilingIdSelector,
  store,
  useAppDispatch,
  useAppSelector,
  useCheckoutCountryCode,
  useConsumerSelector,
  useCountry,
  useShouldInterceptExternalLinks,
} from '../src/state'
import { initPolyFill } from '../src/utils/polyfill'
import dynamic from 'next/dynamic'
import { datadogRum } from '@datadog/browser-rum'
import { DataDog } from '@afterpaytouch/integrations/datadog'
import { Country } from '@afterpay/types'
import { DevTools, ExternalLinkIframeModal, GlobalLinkInterceptor } from '../src/component'
import Head from 'next/head'
import { getFaviconHref } from '../server/favicon'
import { FlowProvider } from '../src/context/FlowContext'
import { CheckoutFlow } from '../src/flow/type'
import { CheckoutLaunchDarklyContext } from '../src/providers/CheckoutLaunchDarklyContext'
import { CheckoutJourneyAnchors, UserJourneyProvider } from '../src/integrations/userJourney'
import './global.css'

initPolyFill()

const ldConfig = getLaunchDarklyConfiguration({
  clientSideID: app.LAUNCH_DARKLY_CLIENT_SIDE_ID,
  options: {
    logger: {
      // We don't want LD console logs to appear as errors in Datadog so set the logging severity to info for all messages.
      error: (message) => console.info(message),
      warn: (message) => console.info(message),
      info: () => {},
      debug: () => {},
    },
    sendEvents: !(DEBUG_MODE as boolean),
    inspectors: [
      {
        type: 'flag-used',
        name: 'dd-inspector',
        method: (key: string, detail: LDEvaluationDetail) => {
          datadogRum.addFeatureFlagEvaluation(key, detail.value)
        },
      },
    ],
  },
})

type Layout = (page: ReactElement) => ReactElement

const isDebugMode: boolean = DEBUG_MODE

interface CustomPageProps {
  theme: Theme
  flowName?: CheckoutFlow
}

interface PageComponentProps {
  flowName?: CheckoutFlow
}

type CustomAppComponent = NextComponentType<NextPageContext, AppInitialProps, PageComponentProps> & {
  getLayout?: Layout
}

const DataDogWrapper = (): ReactElement => {
  const { accountCountry: consumerCountryCode, uuid } = useConsumerSelector() ?? {}
  const merchantCountryCode = useCheckoutCountryCode() as Country

  return (
    <DataDog
      userProperties={{
        uuid,
        merchantCountryCode,
        consumerCountryCode,
      }}
      appValues={app}
      allowedTracingUrls={[api.PORTAL_API_BASE_URL, api.VERIFY_SERVICE_BASE_URL]}
      useCountry={useCountry}
    />
  )
}

const mainLayout: Layout = (page) => <MainLayout>{page}</MainLayout>

const ModalsProvider = ({ children }): ReactNode => {
  const modals = useAppSelector((state) => state.modal)
  const dispatch = useAppDispatch()

  return (
    <ModalProvider modals={modals} dispatch={dispatch}>
      {children}
    </ModalProvider>
  )
}

const ExternalLinkInterceptor = (): ReactElement | null => {
  const shouldInterceptExternalLinks = useShouldInterceptExternalLinks()

  if (!shouldInterceptExternalLinks) {
    return null
  }

  return (
    <>
      <GlobalLinkInterceptor />
      <ExternalLinkIframeModal />
    </>
  )
}

interface CustomAppProps extends AppPropsType<Router, CustomPageProps> {
  Component: CustomAppComponent
  router: Router
}

type CheckoutAppType = NextComponentType<AppContextType, AppInitialProps, CustomAppProps>

const ThemedFavicon = (): ReactElement => {
  const {
    theme: { selected },
  } = useTheme()

  const country = useCountry()

  return (
    <Head>
      <link rel='icon' type='image/x-icon' href={getFaviconHref(selected, country)} />
    </Head>
  )
}

const CheckoutApp: CheckoutAppType = ({ Component, pageProps }) => {
  // On SSR, create a fresh store per request to prevent RTK Query state from accumulating
  // across requests in the module-level singleton. On client, use the module-level singleton
  // so it stays in sync with axios interceptors set up in request.ts.
  const storeRef = useRef<ReturnType<typeof makeStore> | null>(null)
  if (storeRef.current === null) {
    storeRef.current = typeof window === 'undefined' ? makeStore() : store
  }
  const appStore = storeRef.current

  const { getLayout: ComponentLayout } = Component
  const getLayout = ComponentLayout ?? mainLayout
  const profilingSessionId = profilingIdSelector(appStore.getState())
  const { theme, key, ...remainingPageProps } = pageProps as typeof pageProps & { key?: string }

  // Set prelogin cookie for anonymous users
  useEffect(() => {
    if (typeof window === 'undefined' || !key) return

    const cookies = parseCookies()
    const hasUserId = cookies[APT_USER_ID]
    const hasPreloginUserId = cookies[APT_PRELOGIN_USER_ID]

    // Set prelogin cookie only if user doesn't have apt-user-id and prelogin cookie doesn't exist
    if (!hasUserId && !hasPreloginUserId) {
      setCookie(null, APT_PRELOGIN_USER_ID, key, {
        path: '/',
        sameSite: 'none',
        secure: true,
      })
    }
  }, [key])

  const initialDarkMode = getInitialDarkMode()

  if (typeof window !== 'undefined') {
    /* Pages which followed other pages with the HoC "withPublic/withPreventBrowserNavigation" components were still listening to
    router.beforePopState() despite not being intended. This sets the default listener for all pages to not route unless overridden. */
    router.beforePopState(() => {
      return true
    })
  }

  const DynamicThreatMatrix = dynamic(async () => await import('@afterpaytouch/integrations/threat-matrix').then((module) => module.ThreatMatrix), {
    ssr: false,
  })

  const flowName = pageProps.flowName ?? 'default'

  return (
    <Provider store={appStore}>
      <OneTrust />
      <GoogleTagManager />
      <Kasada src={app.KASADA_SRC} urlList={[api.PORTAL_API_BASE_URL, api.VERIFY_SERVICE_BASE_URL]} />
      {flowName === 'default' && <DynamicThreatMatrix profilingSessionId={profilingSessionId} tmOrganisationId={app.TM_ORGANISATION_ID} />}
      <DataDogWrapper />
      <DyScan />
      <FlowProvider flowName={flowName}>
        <ThemeProvider theme={theme} darkMode={initialDarkMode} getDarkModePreference={getDarkModePreference} setDarkModePreference={setDarkModePreference}>
          <ThemedFavicon />
          <PersistGate persistor={persistor as Persistor} loading={<Loader alignCenter />}>
            <AmplitudeProvider>
              <CheckoutLaunchDarklyContext>
                <UserJourneyProvider>
                  <CheckoutJourneyAnchors />
                  <ModalsProvider>
                    <TooltipProvider>
                      <ExternalLinkInterceptor />
                      {getLayout(<Component key={key} {...remainingPageProps} />)}
                      <ModalContainer />
                      {isDebugMode ? <DevTools /> : null}
                    </TooltipProvider>
                  </ModalsProvider>
                </UserJourneyProvider>
              </CheckoutLaunchDarklyContext>
            </AmplitudeProvider>
          </PersistGate>
        </ThemeProvider>
      </FlowProvider>
    </Provider>
  )
}

const withTranslation: CheckoutAppType = appWithTranslation(CheckoutApp, nextI18NextConfig as UserConfig)

export default withLDProvider(ldConfig)(withTranslation)
